// src/components/pages/userpage/partials/UserPostsList.jsx (FIXED FILE)

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // 💡 useTranslation 훅 임포트
import styles from '../style/UserPage.module.css';

const API_URL = process.env.REACT_APP_API_BASE_URL;
const PAGE_SIZE = 5;

export default function UserPostsList({ userId, nickname }) {
  const { t } = useTranslation(); // 💡 useTranslation 훅 사용
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [nextToken, setNextToken] = useState(null);
  const loadingRef = useRef(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadUserPosts = useCallback((reset = false) => {
    if (loadingRef.current) return;
    if (!reset && !hasMore) return;

    loadingRef.current = true;
    if (reset) {
      setInitialLoading(true);
      setError(null);
    }

    const params = new URLSearchParams({
      sortBy: 'latest',
      limit: PAGE_SIZE,
      userId: userId,
    });
    if (!reset && nextToken) params.append('lastEvaluatedKey', nextToken);

    const url = `${API_URL}/posts?${params.toString()}`;

    fetch(url)
      .then(res => {
        if (!res.ok) throw new Error(`${t('userPage.userPostsList.loadingError')} Status ${res.status}: ${res.statusText}`);
        return res.json();
      })
      .then(data => {
        const items = data.items || [];
        if (!Array.isArray(items)) {
          console.error('Expected items to be an array, got:', items);
          throw new Error('Invalid data format: items is not an array');
        }
        setPosts(prev => (reset ? items : [...prev, ...items]));
        setNextToken(data.nextToken || null);
        setHasMore(!!data.nextToken);
      })
      .catch(err => {
        console.error('Error loading user posts:', err.message, err.stack);
        setError(t('userPage.userPostsList.loadingError', { error: err.message }));
        setPosts([]);
        setHasMore(false);
      })
      .finally(() => {
        loadingRef.current = false;
        setInitialLoading(false);
      });
  }, [userId, nextToken, hasMore, t]);

  useEffect(() => {
    if (userId) {
      setPosts([]);
      setNextToken(null);
      setHasMore(true);
      loadUserPosts(true);
    }
  }, [userId]);

  useEffect(() => {
    const onScroll = () => {
      if (loadingRef.current || !hasMore) return;
      const { scrollTop, clientHeight, scrollHeight } = document.documentElement;
      if (scrollTop + clientHeight >= scrollHeight - 300) {
        loadUserPosts(false);
      }
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [hasMore, loadUserPosts]);

  if (initialLoading && posts.length === 0) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>{t('userPage.userPostsList.loadingPosts')}</p>
      </div>
    );
  }

  if (error) {
    return <div className={styles.errorMessage}>{error}</div>;
  }

  return (
    <section className={styles.userPostsSection}>
      {/* 💡 i18n 적용: nickname과 게시글 수를 표시합니다 */}
      {/* {posts.length > 0 && (
        <h2 className={styles.userPostsTitle}>
          {t('userPage.userPostsList.postCount', { nickname: nickname || t('userPage.unknownUser'), count: posts.length })}
        </h2>
      )} */}
      
      {posts.length > 0 ? (
        <div className={styles.postFeed}>
          {posts.map(post => (
            <article
              key={post.postId}
              className={styles.postItem}
              onClick={() => navigate(`/posts/${post.postId}`)}
            >
              <div className={styles.postContent}>
                <div className={styles.textContent}>
                  <h2>{post.title}</h2>
                  {post.previewTitle && (
                    <p className={styles.preview}>{post.previewTitle.substring(0, 200)}...</p>
                  )}
                  <div className={styles.meta}>
                    <span className={styles.likes}>{post.likes || 0}</span>
                    <span className={styles.comments}>{post.commentCount || 0}</span>
                    {/* 💡 날짜 형식은 그대로 유지 */}
                    <span className={styles.date}>🗓️ {new Date(post.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
                {post.previewImageUrl && (
                  <div className={styles.previewImageWrapper}>
                    <img
                      src={post.previewImageUrl}
                      alt={`Preview for ${post.title}`}
                      className={styles.previewImage}
                    />
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      ) : (
        !initialLoading && <p className={styles.emptyMessage}>{t('userPage.userPostsList.noPosts')}</p>
      )}
      {loadingRef.current && posts.length > 0 && (
        <p className={styles.loadingMessage}>{t('userPage.userPostsList.loadingMore')}</p>
      )}
      {!hasMore && posts.length > 0 && <p className={styles.endMessage}>{t('userPage.userPostsList.endOfPosts')}</p>}
    </section>
  );
}